"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, Typography, Button, Grid, Box, IconButton, Skeleton, CircularProgress, Chip } from "@mui/material"
import { motion } from "framer-motion"
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton, IonIcon, useIonViewWillEnter } from "@ionic/react"
import { refreshOutline } from "ionicons/icons"
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Group,
  Assessment,
  AccountBalance,
  Receipt,
  Dashboard,
  Analytics,
  Timeline,
  Notifications,
  Settings,
  Business,
  AccountTree,
  EmojiPeople,
  Payment,
  Savings,
} from "@mui/icons-material"
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import axios from "axios"
import { requestID, baseUrl, config } from "../../common/utils"
import { formatCurrencyN } from "../../common/utils_t"

// Mock data for cooperative dashboard
const cooperativeData = {
  members: 1250,
  savings: 4500000,
  loans: 2800000,
  shares: 1750000,
  growthRate: 8.5,
  activeLoans: 345,
  pendingApplications: 42,
  defaultRate: 2.3,
}

const memberGrowthData = [
  { month: "Jan", members: 950, savings: 3200000 },
  { month: "Feb", members: 1020, savings: 3500000 },
  { month: "Mar", members: 1080, savings: 3800000 },
  { month: "Apr", members: 1120, savings: 3950000 },
  { month: "May", members: 1180, savings: 4100000 },
  { month: "Jun", members: 1250, savings: 4500000 },
]

const loanTypeData = [
  { name: "Emergency", value: 35, color: "#8884d8" },
  { name: "Education", value: 25, color: "#82ca9d" },
  { name: "Business", value: 20, color: "#ffc658" },
  { name: "Agriculture", value: 12, color: "#ff7300" },
  { name: "Housing", value: 8, color: "#00ff88" },
]

const repaymentData = [
  { day: "Mon", onTime: 45, late: 5 },
  { day: "Tue", onTime: 52, late: 3 },
  { day: "Wed", onTime: 38, late: 7 },
  { day: "Thu", onTime: 61, late: 4 },
  { day: "Fri", onTime: 73, late: 6 },
  { day: "Sat", onTime: 42, late: 8 },
  { day: "Sun", onTime: 35, late: 9 },
]

const recentActivities = [
  { id: 1, type: "Loan Application", member: "John Doe", amount: 250000, time: "2 hours ago", status: "pending" },
  { id: 2, type: "Savings Deposit", member: "Jane Smith", amount: 50000, time: "4 hours ago", status: "completed" },
  { id: 3, type: "Loan Repayment", member: "Mike Johnson", amount: 75000, time: "6 hours ago", status: "completed" },
  { id: 4, type: "Share Purchase", member: "Sarah Wilson", amount: 100000, time: "8 hours ago", status: "completed" },
  { id: 5, type: "Withdrawal", member: "Tom Brown", amount: -30000, time: "1 day ago", status: "completed" },
]

interface UserData {
  firstName: string
  middleName: string
  lastName: string
}

const CooperativePage: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>({ firstName: "Cooperative", middleName: "", lastName: "Manager" })
  const [loading, setLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [selectedTimeframe, setSelectedTimeframe] = useState("7d")
  const [coopData, setCoopData] = useState(cooperativeData)
  const [loaddata, setLoadData] = useState(false)

  useEffect(() => {
    // Simulate loading user data
    const storedData = localStorage.getItem("userData")
    if (storedData) {
      setUserData(JSON.parse(storedData))
    }
  }, [])

  const handleRefresh = () => {
    setLoading(true)
    setTimeout(() => {
      setLastRefresh(new Date())
      setLoading(false)
    }, 1000)
  }

  const quickMenuItems = [
    { icon: <Dashboard />, label: "Dashboard", color: "#1976d2", count: null },
    { icon: <People />, label: "Members", color: "#388e3c", count: coopData.members.toString() },
    { icon: <Savings />, label: "Savings", color: "#f57c00", count: formatCurrencyN(coopData.savings) },
    { icon: <AccountBalance />, label: "Loans", color: "#7b1fa2", count: formatCurrencyN(coopData.loans) },
    { icon: <Business />, label: "Shares", color: "#d32f2f", count: formatCurrencyN(coopData.shares) },
    { icon: <Receipt />, label: "Transactions", color: "#0288d1", count: "156" },
    { icon: <Notifications />, label: "Applications", color: "#e91e63", count: coopData.pendingApplications.toString() },
    { icon: <Settings />, label: "Settings", color: "#795548", count: null },
  ]

  const timeframeOptions = [
    { value: "24h", label: "24 Hours" },
    { value: "7d", label: "7 Days" },
    { value: "30d", label: "30 Days" },
    { value: "90d", label: "90 Days" },
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success"
      case "pending":
        return "warning"
      case "rejected":
        return "error"
      default:
        return "default"
    }
  }

  // Simulate API call to fetch cooperative data
  const fetchCooperativeData = async () => {
    setLoading(true)
    try {
      // In a real app, you would make an API call here
      setTimeout(() => {
        setCoopData(cooperativeData)
        setLoadData(true)
        setLoading(false)
      }, 1500)
    } catch (error) {
      console.error("Error fetching cooperative data:", error)
      setLoading(false)
    }
  }

  useIonViewWillEnter(() => {
    fetchCooperativeData()
  }, [])

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle>🤝 Cooperative Dashboard - Welcome, {userData ? `${userData.firstName}` : "Manager"}!</IonTitle>
          <IonButtons slot="end">
            <Typography variant="caption" style={{ marginRight: "10px", color: "white" }}>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </Typography>
            <IonButton fill="clear" onClick={handleRefresh} disabled={loading}>
              <IonIcon
                icon={refreshOutline}
                style={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                  color: "white",
                }}
              />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <Box
          sx={{
            padding: "20px",
            maxWidth: "1400px",
            margin: "0 auto",
            paddingBottom: "40px",
          }}
        >
          {/* Quick Menu Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              🚀 Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {quickMenuItems.map((item, index) => (
                <Grid item xs={6} sm={4} md={3} lg={1.5} key={index}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Card
                      sx={{
                        p: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: item.color,
                        color: "white",
                        position: "relative",
                        "&:hover": { opacity: 0.9 },
                        minHeight: "80px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      {item.count && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "#ff4444",
                            color: "white",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          {item.count}
                        </Box>
                      )}
                      {item.icon}
                      <Typography variant="caption" display="block" sx={{ mt: 1, fontSize: "11px" }}>
                        {item.label}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Key Metrics Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: "#1976d2" }}>
              📊 Cooperative Performance Indicators
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e3f2fd", borderLeft: "5px solid #1976d2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#1976d2">
                        {loaddata ? coopData.members.toLocaleString() : <CircularProgress size={28} thickness={5} />}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Members
                      </Typography>
                    </Box>
                    <Group sx={{ fontSize: 50, color: "#1976d2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +{coopData.growthRate}% from last month
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#e8f5e8", borderLeft: "5px solid #388e3c" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#388e3c">
                        {loaddata ? formatCurrencyN(coopData.savings) : <CircularProgress size={28} thickness={5} />}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Savings
                      </Typography>
                    </Box>
                    <Savings sx={{ fontSize: 50, color: "#388e3c", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      +12.2% from last quarter
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#fff3e0", borderLeft: "5px solid #f57c00" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#f57c00">
                        {loaddata ? formatCurrencyN(coopData.loans) : <CircularProgress size={28} thickness={5} />}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Loans
                      </Typography>
                    </Box>
                    <AccountBalance sx={{ fontSize: 50, color: "#f57c00", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingUp sx={{ color: "green", mr: 1 }} />
                    <Typography variant="caption" color="green" fontWeight="bold">
                      {coopData.activeLoans} active loans
                    </Typography>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 3, backgroundColor: "#f3e5f5", borderLeft: "5px solid #7b1fa2" }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" fontWeight="bold" color="#7b1fa2">
                        {loaddata ? `${coopData.defaultRate}%` : <CircularProgress size={28} thickness={5} />}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Default Rate
                      </Typography>
                    </Box>
                    <Assessment sx={{ fontSize: 50, color: "#7b1fa2", opacity: 0.7 }} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={2}>
                    <TrendingDown sx={{ color: "red", mr: 1 }} />
                    <Typography variant="caption" color="red" fontWeight="bold">
                      -0.5% from last month
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Member Growth Chart */}
            <Grid item xs={12} lg={8}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color="#1976d2">
                      📈 Member Growth & Savings Trend
                    </Typography>
                    <IconButton onClick={handleRefresh} size="small">
                      <IonIcon icon={refreshOutline} />
                    </IconButton>
                  </Box>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={memberGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis yAxisId="left" stroke="#666" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="members"
                        stackId="1"
                        stroke="#8884d8"
                        fill="#8884d8"
                        fillOpacity={0.6}
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="savings"
                        stackId="2"
                        stroke="#82ca9d"
                        fill="#82ca9d"
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Loan Type Distribution */}
            <Grid item xs={12} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    🥧 Loan Types Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={loanTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {loanTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Repayment Performance */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    📅 Weekly Repayment Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={repaymentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="onTime" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="late" fill="#ff7300" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>

            {/* Member Activity */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                  <Typography variant="h6" fontWeight="bold" color="#1976d2" mb={3}>
                    👥 Member Activity Heatmap
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={repaymentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="day" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey="onTime" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Recent Activities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Card sx={{ p: 3, backgroundColor: "white", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold" color="#1976d2">
                  💼 Recent Member Activities
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Timeline />}>
                  View All Activities
                </Button>
              </Box>
              <Box>
                {recentActivities.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ backgroundColor: "#f8f9fa" }}
                    style={{ padding: "16px", borderRadius: "12px", marginBottom: "12px", border: "1px solid #eee" }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            backgroundColor: activity.type.includes("Loan") ? "#e8f5e8" : "#e3f2fd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 3,
                          }}
                        >
                          {activity.type.includes("Loan") ? (
                            <AccountBalance sx={{ color: "#388e3c", fontSize: 24 }} />
                          ) : (
                            <Savings sx={{ color: "#1976d2", fontSize: 24 }} />
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {activity.type}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {activity.member} • {activity.time}
                          </Typography>
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={activity.amount > 0 ? "#388e3c" : "#d32f2f"}
                        >
                          {activity.amount > 0 ? "+" : ""}
                          {formatCurrencyN(Math.abs(activity.amount))}
                        </Typography>
                        <Chip
                          label={activity.status}
                          size="small"
                          color={getStatusColor(activity.status) as any}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Card>
          </motion.div>
        </Box>

        <style jsx>{`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </IonContent>
    </IonPage>
  )
}

export default CooperativePage